/**
 * RES-46 — Sugerencias de insumos desde sucursales.
 *
 * Con el catálogo bloqueado (`catalogLocked`) la sucursal no crea insumos: los sugiere y la
 * organización aprueba (se crea el insumo en el catálogo de todos los locales) o rechaza con
 * motivo. Mismas reglas de validación que el backend (`services/inventory/shared/suggestions.js`).
 */

import { CANONICAL_UNITS } from './units';
import { KINDS } from './inventoryForm';

export const SUGGESTION_STATUSES = ['pending', 'approved', 'rejected'];
export const NAME_MAX = 120;
export const CATEGORY_MAX = 64;
export const REASON_MAX = 300;

/** Estado vacío del formulario de sugerencia. */
export const EMPTY_SUGGESTION_FORM = {
  name: '',
  unit: '',
  category: '',
  kind: '',
  reason: '',
};

const trim = (value) => (value === undefined || value === null ? '' : String(value).trim());

/**
 * Valida el formulario de sugerencia.
 * @param {Object} form - `{ name, unit, category, kind, reason }`
 * @returns {Object} Errores por campo con el `code` del backend (`REQUIRED`, `TOO_LONG`, `INVALID_UNIT`)
 */
export function validateSuggestionForm(form) {
  const errors = {};
  const name = trim(form.name);
  if (!name) errors.name = 'REQUIRED';
  else if (name.length > NAME_MAX) errors.name = 'TOO_LONG';
  if (!form.unit) errors.unit = 'REQUIRED';
  else if (!CANONICAL_UNITS.includes(form.unit)) errors.unit = 'INVALID_UNIT';
  if (trim(form.category).length > CATEGORY_MAX) errors.category = 'TOO_LONG';
  if (trim(form.reason).length > REASON_MAX) errors.reason = 'TOO_LONG';
  return errors;
}

/**
 * Body de `POST /inventory/catalog/suggestions`: textos recortados; los opcionales vacíos no viajan.
 * @param {Object} form - Formulario
 * @returns {Object} `{ name, unit, category?, kind?, reason? }`
 */
export function buildSuggestionPayload(form) {
  const payload = { name: trim(form.name), unit: form.unit };
  const category = trim(form.category);
  if (category) payload.category = category;
  if (KINDS.includes(form.kind)) payload.kind = form.kind;
  const reason = trim(form.reason);
  if (reason) payload.reason = reason;
  return payload;
}

/**
 * Formulario de aprobación precargado con lo que pidió la sucursal.
 * @param {Object} suggestion - Sugerencia
 * @returns {Object} `{ name, unit, category, kind, refCost, defaultMinStock }`
 */
export function prepareApprovalForm(suggestion) {
  return {
    name: suggestion?.name || '',
    unit: CANONICAL_UNITS.includes(suggestion?.unit) ? suggestion.unit : 'un',
    category: suggestion?.category || '',
    kind: KINDS.includes(suggestion?.kind) ? suggestion.kind : 'INGREDIENT',
    refCost: '',
    defaultMinStock: '',
  };
}

/**
 * Valida el formulario de aprobación.
 * @param {Object} form - Formulario de aprobación
 * @returns {Object} Errores por campo (`REQUIRED`, `TOO_LONG`, `INVALID_UNIT`, `INVALID_NUMBER`)
 */
export function validateApprovalForm(form) {
  const errors = {};
  const name = trim(form.name);
  if (!name) errors.name = 'REQUIRED';
  else if (name.length > NAME_MAX) errors.name = 'TOO_LONG';
  if (!CANONICAL_UNITS.includes(form.unit)) errors.unit = 'INVALID_UNIT';
  if (trim(form.category).length > CATEGORY_MAX) errors.category = 'TOO_LONG';
  ['refCost', 'defaultMinStock'].forEach((field) => {
    const raw = trim(form[field]);
    if (raw === '') return;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) errors[field] = 'INVALID_NUMBER';
  });
  return errors;
}

/**
 * `item` de la revisión: solo los campos que la organización cambió respecto de lo sugerido,
 * más costo de referencia y mínimo si se cargaron. Vacío → el backend usa lo sugerido.
 * @param {Object} suggestion - Sugerencia original
 * @param {Object} form - Formulario de aprobación
 * @returns {Object} Campos que pisan la sugerencia
 */
export function buildApprovalOverrides(suggestion, form) {
  const original = prepareApprovalForm(suggestion);
  const item = {};
  const name = trim(form.name);
  if (name && name !== trim(original.name)) item.name = name;
  if (form.unit && form.unit !== (suggestion?.unit || '')) item.unit = form.unit;
  const category = trim(form.category);
  if (category !== trim(suggestion?.category)) item.category = category || null;
  if (KINDS.includes(form.kind) && form.kind !== (suggestion?.kind || 'INGREDIENT')) {
    item.kind = form.kind;
  }
  ['refCost', 'defaultMinStock'].forEach((field) => {
    const raw = trim(form[field]);
    if (raw !== '' && Number.isFinite(Number(raw))) item[field] = Number(raw);
  });
  return item;
}

/**
 * Orden de la bandeja: pendientes primero; dentro de cada estado, las más nuevas arriba.
 * @param {Object[]} suggestions - Sugerencias
 * @returns {Object[]} Copia ordenada
 */
export function sortSuggestions(suggestions) {
  const rank = (s) => {
    const i = SUGGESTION_STATUSES.indexOf(s.status);
    return i === -1 ? SUGGESTION_STATUSES.length : i;
  };
  return [...(suggestions || [])].sort(
    (a, b) =>
      rank(a) - rank(b) || String(b.createdAt || '').localeCompare(String(a.createdAt || ''))
  );
}

/**
 * Cantidad de sugerencias por estado.
 * @param {Object[]} suggestions - Sugerencias
 * @returns {{pending: number, approved: number, rejected: number, all: number}} Contadores
 */
export function countByStatus(suggestions) {
  const counts = { all: 0, pending: 0, approved: 0, rejected: 0 };
  (suggestions || []).forEach((s) => {
    counts.all += 1;
    if (counts[s.status] !== undefined) counts[s.status] += 1;
  });
  return counts;
}

/**
 * Código de error de la respuesta (el `code` crudo, aunque no esté en `inventory.errors`).
 * @param {*} error - Error de axios
 * @returns {string|null} Código
 */
export function responseErrorCode(error) {
  return error?.response?.data?.code || null;
}
